import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import PropTypes from 'prop-types';
import {Collapse} from 'react-collapse';
import {Alert, Button, Form, Input, Select} from 'antd';
import {StyledAddBudgetModal} from './StyledAddBudgetModal';
import EuroInput from '../../../../EuroInput';
import SpinSmall from '../../../../SpinSmall';
import CompanyTagSelect from '../../../TransactionsPage/CompanyTagSelect';
import {formHelpers} from '../../../../../utils/helpers';
import {tagFamilyConstants} from '../../../../../constants';
import {useFormHelpers} from '../../../../../hooks';

const {Item} = Form;

const defaultFormValues = {
  cost_centers: [],
  limit_amount: undefined,
  name: '',
  reported_by: undefined
};

const defaultError = {
  message: null,
  open: false
};

const AddBudgetModal = ({
  budget,
  handleOk,
  handleCancel,
  loading = false,
  mode = 'add',
  open,
  ...rest
}) => {
  const [t] = useTranslation('main');
  const [form] = Form.useForm();
  const [error, setError] = useState(defaultError);
  const [initialFormValues, setInitialFormValues] = useState(defaultFormValues);

  const {employeeOptions, placeholderEnter, placeholderSelect} = useFormHelpers();

  useEffect(() => {
    if (open && mode === 'edit' && budget) {
      const initialValues = {
        cost_centers: budget?.cost_centers || [],
        limit_amount: budget?.limit_amount || 0,
        name: budget?.name || '',
        reported_by: budget?.reported_by?.email || undefined,
      }
      setInitialFormValues(initialValues);
      form.setFieldsValue(initialValues);
    }
  }, [form, mode, open, budget]);

  const handleAfterOpenChange = (open) => {
    if (!open) {
      form.setFieldsValue(defaultFormValues);
      setInitialFormValues(defaultFormValues);
      if (error) setError(defaultError);
    }
  }

  const handleSubmit = (fields) => {
    handleOk?.(
      fields,
      (err) => {
        if (err.message) {
          setError({ ...error, message: err.message, open: true });
        } else {
          const fields = formHelpers.getFormServerErrorFields(err);
          form.setFields(fields);
        }
      }
    )
  }

  const onChangeTags = (tags) => {
    form.setFieldValue('cost_centers', tags);
    setInitialFormValues({...initialFormValues, cost_centers: tags});
  }

  const rules = [{required: true, message: t('validation.fieldIsRequired')}];

  const actionTitle = t(mode === 'add' ? 'create' : 'edit');

  const footer = [
    <Button
      key='next'
      disabled={loading}
      onClick={() => form.submit()}
      size='large'
      type='primary'
    >
      {actionTitle}
    </Button>
  ];

  return (
    <StyledAddBudgetModal
      afterOpenChange={handleAfterOpenChange}
      footer={footer}
      forceRender
      onClose={handleCancel}
      open={open}
      title={`${actionTitle} ${t('Budget')}`}
      width={480}
      {...rest}
    >
      <SpinSmall spinning={loading}>
        <Form
          initialValues={initialFormValues}
          form={form}
          layout='vertical'
          onFinish={handleSubmit}
          requiredMark={false}
        >
          <Item
            label={`${t('Budget')} ${t('name')}`}
            name='name'
            required
            rules={rules}
          >
            <Input placeholder={placeholderEnter('name')} size='large' />
          </Item>
          <Item
            label={`${t('Budget')} ${t('Owner')}`}
            name='reported_by'
            required
            rules={rules}
          >
            <Select
              optionFilterProp='optionLabel'
              options={employeeOptions}
              placeholder={placeholderSelect('reportsTo')}
              size='large'
              showSearch
            />
          </Item>
          <Item
            label={t('costCenter')}
            name='cost_centers'
            required
            rules={rules}
          >
            <CompanyTagSelect
              dropdownProps={{ overlayStyle: null }}
              inputProps={{
                placeholder: placeholderSelect('team'),
                size: 'large'
              }}
              tagFamily={tagFamilyConstants.COST_CENTER}
              variant='multiple'
              onChange={onChangeTags}
              selected={initialFormValues.cost_centers}
            />
          </Item>
          <Item
            label={`${t('Budget')} ${t('limit')}`}
            name='limit_amount'
            required
            rules={rules}
          >
            <EuroInput placeholder={placeholderEnter('limit')} />
          </Item>
        </Form>
        <Collapse isOpened={error.open}>
          <Alert message={error.message} type='error' showIcon />
        </Collapse>
      </SpinSmall>
    </StyledAddBudgetModal>
  );
}

AddBudgetModal.propTypes = {
  budget: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    reported_by: PropTypes.object,
    cost_centers: PropTypes.array,
    limit_amount: PropTypes.number,
  }),
  handleCancel: PropTypes.func,
  handleOk: PropTypes.func,
  loading: PropTypes.bool,
  mode: PropTypes.oneOf(['add', 'edit']),
  open: PropTypes.bool.isRequired,
}

export default AddBudgetModal;
